import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import { Navigate, useNavigate } from 'react-router-dom';

import LearnerDashboardNoDataPlaceholder from '@/client/components/learners/dashboard/components/LearnerDashboardNoDataPlaceholder';
import LearnerDashboardSubscriptionListItem from '../components/subscriptions/LearnerDashboardSubscriptionListItem';
import LearnerDashboardSubscriptionsBrowseList from '@/client/components/learners/dashboard/components/subscriptions/LearnerDashboardSubscriptionsBrowseList';
import OutlineButton from '@/client/components/shared/buttons/OutlineButton';
import ProductsService from '@/client/services/api/graphql/ProductsService';
import { useAuthStore } from '@/client/services/state/authStore';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// TODO: Pull this query out into a hook that is reused everywhere purchases are queried by userId

export default function LearnerDashboardSubscriptions() {
  const { t } = useTranslation();
  const { authConfig } = useAuthStore();
  const { user } = authConfig;
  const { is_purchaser: isPurchaser, _id: userId } = user;
  const navigate = useNavigate();

  if (!isPurchaser) {
    return <Navigate to="/dashboard" replace />;
  }

  const { data, isLoading, isError } = useQuery({
    queryKey: ['user-purchases', userId],
    queryFn: () =>
      ProductsService.listPurchases({
        user: { in: [userId] },
        subscriptionActive: true,
        page: 0,
        pageSize: 10,
        includeTotalCount: true,
      }),
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    return <div>Error</div>;
  }

  const { purchases } = data;

  return (
    <Stack spacing={8}>
      <Text fontSize="24px" fontWeight="600" color="baseBlack">
        {t('learners.dashboard.subscriptions.yourSubscriptions')}
      </Text>

      {purchases.length === 0 && (
        <LearnerDashboardNoDataPlaceholder
          imagePath="/public/images/Subscribe-2-Streamline-Brooklyn.svg"
          title={t('learners.dashboard.subscriptions.noData.title')}
          helpText={t('learners.dashboard.subscriptions.noData.helpText')}
          buttonData={undefined}
        />
      )}

      {purchases.length > 0 && (
        <Stack>
          <Text fontSize="20px" fontWeight={600} color="baseBlack">
            Active Subscriptions
          </Text>

          <HStack spacing={4} gap={4} flexWrap="wrap">
            {purchases.map((purchase) => (
              <Box
                minWidth={{ base: 'full', md: '50%', lg: '33%' }}
                maxWidth={{ base: 'full', md: '50%', lg: '33%' }}
                width={{ base: 'full', md: '50%', lg: '33%' }}
              >
                <LearnerDashboardSubscriptionListItem
                  key={purchase.id}
                  orientation="vertical"
                  title={purchase.productDetails.title}
                  description={purchase.productDetails.description}
                  price={purchase.productDetails.price}
                  subscriptionDuration={purchase.productDetails.subscriptionDuration}
                  image={purchase.productDetails.images[0] || '/public/images/cardPlaceholder.png'}
                  actionGroup={
                    <Stack width="full">
                      <OutlineButton
                        size="xs"
                        onClick={() => navigate(`/manage-subscription/${purchase.id}`)}
                      >
                        {t('ecommerce.productCard.manageSubscription')}
                      </OutlineButton>

                      <OutlineButton size="xs" onClick={() => navigate(`/payment-history`)}>
                        {t('ecommerce.productCard.viewSubscriptionPaymentHistory')}
                      </OutlineButton>

                      {purchase.nextScheduledPayment && (
                        <Stack>
                          <Text>{`${t('ecommerce.productCard.nextBillingDate')}: ${
                            purchase.nextScheduledPayment
                          }`}</Text>

                          <Text>{t('ecommerce.productCard.renewedAutomatically')}</Text>
                        </Stack>
                      )}
                    </Stack>
                  }
                />
              </Box>
            ))}
          </HStack>
        </Stack>
      )}

      <LearnerDashboardSubscriptionsBrowseList />
    </Stack>
  );
}
